import { getTriageQuestions, postTriageAnswer } from '@cannect/services/resources/instantConsultation/requests'
import { TTriageQuestion } from '@cannect/services/resources/instantConsultation/types'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useInstantConsultation } from 'hooks/useInstantConsultation'
import useSignedUser from 'hooks/useSignedUser'

type FormData = {
  answers: Record<string, string>
  followUps: Record<string, string>
}

function normalizeQuestions(raw: TTriageQuestion[]): TTriageQuestion[] {
  return raw.map((question) => {
    if (question.id === 4) {
      return {
        ...question,
        type: 'options' as const,
        options: ['Não, nunca usei.', 'Sim, e nunca tive nenhum efeito adverso. ', 'Sim, e tive efeitos adversos.']
      }
    }
    if (question.id === 2) {
      return {
        ...question,
        type: 'options' as const,
        options: Array.from({ length: 11 }, (_, i) => i.toString())
      }
    }
    return question
  })
}

function buildDefaultAnswers(questionsWithOptions: TTriageQuestion[]): Record<string, string> {
  return questionsWithOptions.reduce(
    (acc, question) => {
      if (question.type === 'boolean') {
        acc[question.id] = 'Não'
      } else if (question.id === 2) {
        acc[question.id] = '0'
      } else if (question.id === 4) {
        acc[question.id] = 'Não, nunca usei.'
      }
      return acc
    },
    {} as Record<string, string>
  )
}

const MODAL_DESCRIPTION =
  'Obrigado por responder esse questionário. \n\n Sua consulta com o médico, realizada por video chamada com toda segurança e comodidade, tem o valor de R$ 99,00. \n\n Atenção: após concluir o pagamento, o médico iniciará o atendimento na hora. Certifique-se de que você pode entrar na consulta imediatamente.'

export function QuestionnaireStep() {
  const { session, goTo, setQuestionnaireCompleted } = useInstantConsultation()
  const { signedUser } = useSignedUser()
  const [questions, setQuestions] = useState<TTriageQuestion[]>([])
  const [currentStep, setCurrentStep] = useState(0)
  const [loadingQuestions, setLoadingQuestions] = useState(true)
  const [questionsLoadError, setQuestionsLoadError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const { watch, setValue, register } = useForm<FormData>({
    defaultValues: { answers: {}, followUps: {} }
  })

  const answers = watch('answers')
  const followUps = watch('followUps')

  useEffect(() => {
    const load = async () => {
      setLoadingQuestions(true)
      try {
        const res = await getTriageQuestions()
        const normalized = normalizeQuestions(res)
        setQuestions(normalized)
        const defaults = buildDefaultAnswers(normalized)
        setValue('answers', defaults, { shouldDirty: true })
      } catch {
        setQuestionsLoadError(true)
        toast.error('Erro ao carregar perguntas.')
      } finally {
        setLoadingQuestions(false)
      }
    }
    load()
  }, [setValue])

  const currentQuestion = questions[currentStep]
  const answer = currentQuestion ? answers[currentQuestion.id] : ''

  const showFollowUp = useMemo(() => {
    if (!currentQuestion?.follow_up_question || !currentQuestion?.response_question) return false
    return currentQuestion.response_question.toLowerCase() === answer?.toLowerCase()
  }, [currentQuestion, answer])

  const isRequired = currentQuestion?.type === 'text' || currentQuestion?.id === 4

  const handleBooleanAnswer = useCallback(
    (id: number, checked: boolean) => {
      setValue(
        'answers',
        {
          ...answers,
          [id]: checked ? 'Sim' : 'Não'
        },
        { shouldDirty: true }
      )
    },
    [answers, setValue]
  )

  const handleOptionAnswer = useCallback(
    (id: number, option: string) => {
      setValue(
        'answers',
        {
          ...answers,
          [id]: option
        },
        { shouldDirty: true }
      )
    },
    [answers, setValue]
  )

  const onSubmitStep = async () => {
    if (!session?.id || !currentQuestion) return

    if (isRequired && !answers[currentQuestion.id]) {
      toast.error('Por favor, responda a pergunta antes de continuar.')
      return
    }

    if (
      currentQuestion.id === 4 &&
      answer === 'Sim, e tive efeitos adversos.' &&
      !followUps[currentQuestion.id]?.trim()
    ) {
      toast.error('Por favor, preencha o campo complementar.')
      return
    }

    setIsSubmitting(true)
    try {
      const mainAnswer = answers[currentQuestion.id]
      const hasFollowUp =
        Boolean(currentQuestion.follow_up_question) &&
        currentQuestion.response_question?.toLowerCase() === mainAnswer?.toLowerCase()

      await postTriageAnswer(currentQuestion.id, {
        answer: mainAnswer,
        ...(hasFollowUp && {
          additional_question: followUps[currentQuestion.id]
        }),
        session_id: session.id
      })

      if (currentStep < questions.length - 1) {
        setCurrentStep((s) => s + 1)
      } else {
        setModalOpen(true)
      }
    } catch {
      toast.error('Erro ao enviar resposta.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1)
    } else {
      goTo('triage')
    }
  }

  const firstName = signedUser?.name?.split(' ')[0] ?? ''

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center text-sm text-gray-600">
        Sessão indisponível.{' '}
        <button type="button" className="text-primary-700 underline" onClick={() => goTo('triage')}>
          Voltar à triagem
        </button>
      </div>
    )
  }

  if (questionsLoadError || (!loadingQuestions && questions.length === 0)) {
    return (
      <div className="mx-auto max-w-md px-4 py-12 text-center text-sm text-gray-600">
        Não foi possível carregar o questionário.{' '}
        <button type="button" className="text-primary-700 underline" onClick={() => goTo('triage')}>
          Voltar à triagem
        </button>
      </div>
    )
  }

  if (loadingQuestions) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
      </div>
    )
  }

  return (
    <div className="mx-auto flex min-h-0 max-w-[600px] flex-1 flex-col bg-gray-50">
      <div className="relative rounded-b-3xl bg-primary-700 px-6 pb-8 pt-10 text-white">
        <button
          type="button"
          onClick={handleBack}
          className="absolute left-4 top-6 rounded-full border-none bg-primary-700 p-2 text-white hover:bg-white/10"
          aria-label="Voltar">
          <ArrowLeft className="h-6 w-6" />
        </button>
        <h1 className="mt-8 text-2xl font-bold leading-tight">
          {firstName ? `${firstName}, responda as perguntas abaixo` : 'Responda as perguntas abaixo'}
        </h1>
      </div>

      <div className="overflow-y-auto px-4 py-6">
        <div className="mx-auto max-w-2xl">
          <div className="mb-6">
            <p className="mb-2 text-sm text-gray-500">
              Pergunta {currentStep + 1} de {questions.length}
            </p>
            <div className="h-2 rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-primary-700 transition-all"
                style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <h2 className="mb-6 text-base font-semibold text-gray-800">
              {currentQuestion.question}
              {isRequired && <span className="text-red-500"> *</span>}
            </h2>

            {currentQuestion.type === 'text' && (
              <textarea
                {...register(`answers.${currentQuestion.id}`, {
                  required: isRequired ? 'Este campo é obrigatório' : false
                })}
                rows={4}
                placeholder="Digite sua resposta"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            )}

            {currentQuestion.type === 'boolean' && (
              <div className="space-y-3" role="radiogroup" aria-label={currentQuestion.question}>
                <label className="flex cursor-pointer items-center gap-3" htmlFor={`bool-yes-${currentQuestion.id}`}>
                  <input
                    id={`bool-yes-${currentQuestion.id}`}
                    type="radio"
                    name={`bool-${currentQuestion.id}`}
                    checked={answer === 'Sim'}
                    onChange={() => handleBooleanAnswer(currentQuestion.id, true)}
                    className="h-5 w-5 border-gray-300 text-primary-700 focus:ring-primary-500"
                  />
                  <span className="font-medium text-gray-800">Sim</span>
                </label>
                <label className="flex cursor-pointer items-center gap-3" htmlFor={`bool-no-${currentQuestion.id}`}>
                  <input
                    id={`bool-no-${currentQuestion.id}`}
                    type="radio"
                    name={`bool-${currentQuestion.id}`}
                    checked={answer === 'Não'}
                    onChange={() => handleBooleanAnswer(currentQuestion.id, false)}
                    className="h-5 w-5 border-gray-300 text-primary-700 focus:ring-primary-500"
                  />
                  <span className="font-medium text-gray-800">Não</span>
                </label>
              </div>
            )}

            {currentQuestion.type === 'options' && (
              <div className="space-y-3" role="radiogroup" aria-label={currentQuestion.question}>
                {currentQuestion.options?.map((option, optIdx) => {
                  const optId = `opt-${currentQuestion.id}-${optIdx}`
                  return (
                    <label key={option} className="flex cursor-pointer items-center gap-3" htmlFor={optId}>
                      <input
                        id={optId}
                        type="radio"
                        name={`q-${currentQuestion.id}`}
                        checked={answer === option}
                        onChange={() => handleOptionAnswer(currentQuestion.id, option)}
                        className="h-5 w-5 border-gray-300 text-primary-700 focus:ring-primary-500"
                      />
                      <span className="font-medium text-gray-800">{option}</span>
                    </label>
                  )
                })}

                {currentQuestion.id === 4 && answer === 'Sim, e tive efeitos adversos.' && (
                  <div className="mt-4 rounded-xl bg-gray-50 p-4">
                    <p className="mb-2 text-sm font-medium text-gray-700">{currentQuestion.follow_up_question}</p>
                    <textarea
                      {...register(`followUps.${currentQuestion.id}`, {
                        required: 'Este campo é obrigatório'
                      })}
                      rows={3}
                      placeholder="Digite sua resposta complementar"
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary-500"
                    />
                  </div>
                )}
              </div>
            )}

            {showFollowUp && currentQuestion.id !== 4 && (
              <div className="mt-4 rounded-xl bg-gray-50 p-4">
                <p className="mb-2 text-sm font-medium text-gray-700">{currentQuestion.follow_up_question}</p>
                <textarea
                  {...register(`followUps.${currentQuestion.id}`)}
                  rows={3}
                  placeholder="Digite sua resposta complementar"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary-500"
                />
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              onSubmitStep()
            }}
            disabled={isSubmitting}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-800 disabled:opacity-60">
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {currentStep === questions.length - 1 ? 'Enviar respostas' : 'Próxima pergunta'}
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-xl font-bold text-gray-900">Estamos quase lá!</h3>
            <p className="mt-4 whitespace-pre-line text-sm text-gray-600">{MODAL_DESCRIPTION}</p>
            <button
              type="button"
              onClick={() => {
                setModalOpen(false)
                setQuestionnaireCompleted(true)
                goTo('payment')
              }}
              className="mt-6 w-full rounded-lg bg-primary-700 py-3 text-base font-semibold text-white hover:bg-primary-800">
              Ir para o pagamento
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
